import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import ResumeBox from '@/common/components/ResumeBox'
import { formatCpf } from '@/common/utils/formatters'
import { FormTransformers } from '@/common/utils/formTransformers'
import { ModalAddWitness } from '@/features/privateCompanyLeadsFunnel/components/ContractSignatureDetails/ClicksignSignature/ModalAddWitness'
import { useRemoveContractEnvelopeWitness } from '@/features/privateCompanyLeadsFunnel/hooks/useRemoveContractEnvelopeWitness'
import { ContractEnvelopeData } from '@/features/privateCompanyLeadsFunnel/types/privateCompanyLeadContractEnvelope'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircleIcon, XIcon } from 'lucide-react'
import { useState } from 'react'

interface WitnessesProps {
  privateCompanyLeadId: string
  contractEnvelope?: ContractEnvelopeData
}

export const Witnesses = ({ privateCompanyLeadId, contractEnvelope }: WitnessesProps) => {
  const queryClient = useQueryClient()

  const [witnessId, setWitnessId] = useState<string>()
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const onSuccess = () => {
    setWitnessId(undefined)
    queryClient.invalidateQueries({ queryKey: ['privateCompanyLead', privateCompanyLeadId] })
    queryClient.invalidateQueries({ queryKey: ['privateCompanyLeadContractEnvelope', privateCompanyLeadId] })
  }

  const { mutate: removeWitness, isPending: removeWitnessIsPending } = useRemoveContractEnvelopeWitness({
    id: privateCompanyLeadId,
    onSuccess,
    onError: FormTransformers.errorsTransformer({ setMessages }),
  })

  return (
    <>
      <div className="flex flex-col gap-y-2">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <p className="text-sm font-bold">Testemunhas</p>

            <div className="flex items-center gap-x-2">
              <AlertCircleIcon strokeWidth={1} className="size-6 text-warning" />

              <p className="text-[11px] font-bold">Ao menos uma testemunha é obrigatória.</p>
            </div>
          </div>

          <ModalAddWitness privateCompanyLeadId={privateCompanyLeadId} />
        </div>

        {contractEnvelope?.witnesses?.map((witness, index) => (
          <Box key={witness.id} className="flex items-start gap-x-2 p-2">
            <div className="flex w-full flex-col gap-y-2">
              <ResumeBox
                label="Nome"
                value={witness.full_name}
                boxClassName="bg-white-200 rounded-sm p-2"
                labelClassName="font-normal"
                valueClassName="font-bold"
                testId={`witness_full_name_${index}`}
              />

              <div className="flex w-full gap-x-2">
                <ResumeBox
                  label="CPF"
                  value={witness.cpf ? formatCpf(witness.cpf) : '--'}
                  boxClassName="bg-white-200 rounded-sm p-2"
                  labelClassName="font-normal"
                  valueClassName="font-bold truncate"
                />

                <ResumeBox
                  label="Email"
                  value={witness.email ?? '--'}
                  boxClassName="w-full bg-white-200 rounded-sm p-2"
                  labelClassName="font-normal"
                  valueClassName="font-bold break-all"
                />
              </div>
            </div>

            <Button
              type="button"
              name={`remove_witness_${index}`}
              variant="error"
              className="size-9 p-0 shrink-0"
              onClick={() => setWitnessId(witness.id)}
              disabled={removeWitnessIsPending}
            >
              <XIcon className="size-4" />
            </Button>
          </Box>
        ))}
      </div>

      <Modal
        isOpen={!!witnessId}
        setIsOpen={() => setWitnessId(undefined)}
        useCancellableFooter
        showClose={false}
        title="Você tem certeza?"
        subtitle="Essa testemunha será removida e caso necessário será preciso recadastrar-lo"
        titleClass="text-2xl"
        subtitleClassName="mt-1 text-sm"
        confirmButtonText="Deletar testemunha"
        cancelButtonClassName="text-sm"
        confirmButtonClassName="text-sm bg-primary-blue"
        confirmButtonIsLoading={removeWitnessIsPending}
        onCancel={() => setWitnessId(undefined)}
        onConfirm={() => {
          if (witnessId) {
            removeWitness(witnessId)
          }
        }}
      />

      <ModalMessageSimple messages={messages} setMessages={setMessages} isError />
    </>
  )
}
